'use client';
import {useEffect,useRef,useState,type ReactNode} from 'react';
import {usePublicCopy} from './content/navigation';
import {downloads,preparationSteps} from './content/download';
import {TutorialSteps} from './TutorialSteps';
import styles from './public-site.module.css';
import downloadStyles from './download.module.css';
import {useTheme} from 'next-themes';

export function PublicShell({children}:{children:ReactNode}){return <div className={styles.site}>{children}</div>;}
export function PageHeading({eyebrow,title,description}:{eyebrow:string;title:string;description:string}){const {t}=usePublicCopy();return <header className={styles.heading}><span className={styles.eyebrow}>{eyebrow}</span><h1>{t(title)}</h1><p>{t(description)}</p></header>}
export function Unavailable({title,children}:{title:string;children:ReactNode}){const {t}=usePublicCopy();return <section className={styles.unavailable} role="status"><span className={styles.statusDot}/><h2>{t(title)}</h2><p>{children}</p></section>}
// 固定源内页通过公共 Token 跟随站点主题。
export function SourceSurface({children,page}:{children:ReactNode;page:string}){const {resolvedTheme}=useTheme(),[ready,setReady]=useState(false),video=useRef<HTMLVideoElement>(null);useEffect(()=>{setReady(true);const media=matchMedia('(prefers-reduced-motion: reduce)');const sync=()=>{const node=video.current;if(!node)return;node.playbackRate=.3;if(media.matches||document.hidden)node.pause();else node.play().catch(()=>{})};sync();media.addEventListener('change',sync);document.addEventListener('visibilitychange',sync);return()=>{media.removeEventListener('change',sync);document.removeEventListener('visibilitychange',sync)}},[]);return <div className={styles.sourceSurface}><div className="creative-site site-themed" data-color-mode={ready&&resolvedTheme==='dark'?'dark':'light'} data-site-page={page} data-page-video="true"><div className="site-chrome-background" aria-hidden="true"><video ref={video} muted loop playsInline poster="/mac/assets/codex-cloud-background.jpg"><source src="/mac/assets/codex-cloud-background.mp4" type="video/mp4"/></video></div><main className="page-standalone">{children}</main></div></div>}
function Arrow({external=false}:{external?:boolean}) {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={external?'M6 18 18 6M6 6h12v12':'M4 12h16m-6-6 6 6-6 6'}/></svg>;
}

function PlatformIcon({os}:{os:'WIN'|'MAC'}) {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true">{os==='WIN'?<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>:<><rect x="4" y="3" width="16" height="13" rx="2"/><path d="M9 21h6m-3-5v5"/></>}</svg>;
}

export function DownloadPage() {
  const {t}=usePublicCopy();
  return (
    <main className={downloadStyles.page}>
      <div className={downloadStyles.container}>
        <header className={downloadStyles.hero}>
          <div className={downloadStyles.eyebrow}>
            <img src="/mac/assets/codex-cloud-icon.png" alt="" width="36" height="36"/>
            <span>{t('Codex 安装指南')}</span>
          </div>
          <h1>{t('Codex 安装与配置指南')}</h1>
          <p>{t('先确认系统要求与安装准备，正式安装后再连接账户与模型。')}</p>
          <div className={downloadStyles.heroActions}>
            <a className={downloadStyles.primary} href="#installation-preparation">{t('查看安装前准备')}<Arrow/></a>
            <a className={downloadStyles.textLink} href="#full-tutorial">{t('查看完整教程')}<Arrow/></a>
          </div>
        </header>

        <section className={downloadStyles.section} id="download" aria-labelledby="get-assistant">
          <div className={downloadStyles.sectionHeading}>
            <div>
              <h2 id="get-assistant">{t('选择你的操作系统')}</h2>
              <p>{t('客户端安装包待发布。请先核对系统要求，正式发布后再按对应平台安装。')}</p>
            </div>
          </div>
          <div className={downloadStyles.platforms}>
            {downloads.map(item=>(
              <article className={downloadStyles.platform} key={item.os} aria-labelledby={`platform-${item.os}`}>
                <div className={downloadStyles.platformHeading}>
                  <span className={downloadStyles.platformIcon}><PlatformIcon os={item.os}/></span>
                  <div className={downloadStyles.platformTitle}>
                    <h3 id={`platform-${item.os}`}>{item.os==='WIN'?'Windows':'macOS'}</h3>
                    <p>{t(item.desc)}</p>
                  </div>
                  <span className={downloadStyles.badge}>{t('待发布')}</span>
                </div>
                <dl><div><dt>{t('安装说明')}</dt><dd>{t(item.config)}</dd></div></dl>
                <button className={downloadStyles.unavailable} type="button" disabled>{t('安装包暂不可用')}</button>
              </article>
            ))}
          </div>
        </section>

        <section className={downloadStyles.section} id="installation-preparation" aria-labelledby="preparation-title">
          <div className={downloadStyles.sectionHeading}>
            <div>
              <h2 id="preparation-title">{t('安装前准备与配置指引')}</h2>
              <p>{t('先完成准备，再按顺序安装、连接和开始任务。')}</p>
            </div>
          </div>
          <ol className={downloadStyles.steps} role="list">
            {preparationSteps.map(step=>(
              <li key={step.id}>
                <span className={downloadStyles.stepNumber} aria-hidden="true">{step.id}</span>
                <div><h3>{t(step.title)}</h3><p>{t(step.text)}</p></div>
              </li>
            ))}
          </ol>
        </section>

        <div className={downloadStyles.section}>
          <TutorialSteps/>
        </div>

        <div className={downloadStyles.support}>
          <section className={downloadStyles.faq} aria-labelledby="installation-faq">
            <h2 id="installation-faq">{t('常见问题')}</h2>
            <details>
              <summary>{t('现在可以下载安装包吗？')}</summary>
              <div className={downloadStyles.answer}>
                <p>{t('客户端安装包尚未发布，请先查看安装前检查或选择 Skills。获取正式安装包后，再按对应系统的指引安装。')}</p>
              </div>
            </details>
            <details>
              <summary>{t('获取正式安装包后，如何安装？')}</summary>
              <div className={downloadStyles.answer}>
                <p>{t('获取正式发布的 Windows 安装包后，按指引确认备份、安装、启动和连接；安装前请核对正式安装包的系统与架构要求。')}</p>
                <p>{t('获取正式发布的 PKG 后，先退出已有助手，再按系统提示安装；随后在“应用程序”中打开 Codex 助手。安装前请核对正式安装包的系统与架构要求。')}</p>
              </div>
            </details>
            <details>
              <summary>{t('遇到“隐私与安全性”提示怎么办？')}</summary>
              <div className={downloadStyles.answer}>
                <ol>
                  <li>{t('确认安装包来自本站，并确认你信任此来源。')}</li>
                  <li>{t('尝试打开后，进入“系统设置 → 隐私与安全性”，找到刚才被阻止的安装包或应用。')}</li>
                  <li>{t('点击“仍要打开”，按系统提示确认；按钮名称可能因 macOS 版本不同而变化。')}</li>
                </ol>
                <p>{t('若提示“文件已损坏”或“将损坏你的电脑”，请停止安装并联系客服。受单位管理的电脑如无法操作，请联系设备管理员。')}</p>
                <a className={downloadStyles.textLink} href="https://support.apple.com/zh-cn/102445" target="_blank" rel="noopener noreferrer">{t('查看 Apple 操作说明')}<Arrow external/><span className="sr-only">{t('在新窗口打开')}</span></a>
              </div>
            </details>
          </section>
          <aside className={downloadStyles.skills} aria-labelledby="installation-skills">
            <span>{t('Skills 技能')}</span>
            <h2 id="installation-skills">{t('安装之后，从一个任务开始')}</h2>
            <p>{t('先从高频任务开始，再按行业和场景扩展更多能力')}</p>
            <a className={downloadStyles.textLink} href="/skills">{t('挑选任务技能')}<Arrow/></a>
          </aside>
        </div>
      </div>
    </main>
  );
}
